import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, DollarSign, Scale, Shield, Lock, FileText, Download,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { fetchMyInvestment } from "@/repositories/investorRepository";
import type { MyInvestmentData } from "@/domain/investor";
import { formatMoney, formatDate } from "@/utils/format";

const RIGHTS_ICONS: { key: keyof NonNullable<MyInvestmentData["shareClassDetails"]>; label: string; icon: typeof Users }[] = [
  { key: "votingRights", label: "Voting Rights", icon: Users },
  { key: "dividendRights", label: "Dividend Rights", icon: DollarSign },
  { key: "preEmptionRights", label: "Pre-emption Rights", icon: Scale },
  { key: "liquidationRights", label: "Liquidation Rights", icon: Shield },
  { key: "transferRestrictions", label: "Transfer Restrictions", icon: Lock },
  { key: "otherRights", label: "Other Rights", icon: FileText },
];

export function InvestorMyInvestmentPage() {
  const [data, setData] = useState<MyInvestmentData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyInvestment()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <AppShell portal="Investor">
      <h1 style={{ marginTop: 0, fontSize: 28 }}>My Investment</h1>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {!data && !error && <div className="loading-state">Loading live data from Google Sheets…</div>}

      {data && (
        <>
          <div className="dashboard-card" style={{ marginBottom: 18 }}>
            <h3 className="section-title-blue">Your Investment Summary</h3>
            <div className="summary-row-7">
              <SummaryField label="Amount Invested" value={formatMoney(data.amountInvested)} />
              <SummaryField label="Share Premium" value={formatMoney(data.sharePremium)} />
              <SummaryField label="Amount Applied to Shares" value={formatMoney(data.amountAppliedToShares)} />
              <SummaryField label="Shares Owned" value={`${data.sharesOwned.toLocaleString()} ${data.shareClassCode}`} />
              <SummaryField label="Issue Price / Share" value={formatMoney(data.issuePricePerShare)} />
              <Link to="#" onClick={(e) => e.preventDefault()} className="ownership-link-row" style={{ textDecoration: "none", color: "inherit", display: "block", borderRadius: 8 }}>
                <SummaryField label="Ownership (Diluted)" value={data.ownershipPct.toFixed(2) + "%"} />
              </Link>
              <SummaryField label="Estimated Value" value={formatMoney(data.estimatedValue, true)} green />
            </div>
            <div style={{ marginTop: 14, fontSize: 12, color: "var(--text-muted)" }}>
              As of {formatDate(data.asOfDate)}
            </div>
          </div>

          <div className="holdings-two-col" style={{ marginBottom: 18 }}>
            <div className="dashboard-card">
              <h3 className="section-title-blue">Current Holdings</h3>
              {data.holdings.length === 0 && <div className="empty-state">No holdings recorded yet.</div>}
              {data.holdings.map((h) => (
                <div key={`${h.kind}-${h.code}`} style={{ marginBottom: 8 }}>
                  <div className="stat-row" style={{ paddingBottom: 4 }}>
                    <div className="stat-row-left">
                      <div className="circle-badge">{h.code.slice(0, 2).toUpperCase()}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{h.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {h.kind === "Equity" ? "Ordinary Shares" : "Convertible Debt"}
                        </div>
                      </div>
                    </div>
                  </div>
                  {h.kind === "Equity" ? (
                    <div className="holding-meta-row">
                      <div><div>Shares</div><div>{h.shares?.toLocaleString()} {h.code}</div></div>
                      <div><div>Diluted Ownership</div><div>{h.ownershipPct?.toFixed(2)}%</div></div>
                      <div><div>Estimated Value</div><div style={{ color: "#16a34a" }}>{formatMoney(h.estimatedValue, true)}</div></div>
                      <div />
                    </div>
                  ) : (
                    <div className="holding-meta-row">
                      <div><div>Principal Amount</div><div>{formatMoney(h.principalAmount)}</div></div>
                      <div><div>Interest Rate</div><div>{h.interestRate}</div></div>
                      <div><div>Maturity</div><div>{formatDate(h.maturityDate)}</div></div>
                      <div><div>Discount</div><div>{h.discountPct ? h.discountPct.toFixed(0) + "%" : "—"}</div></div>
                    </div>
                  )}
                  {h.kind === "Debt" && (
                    <div style={{ paddingLeft: 50, fontSize: 12.5 }}>
                      <span style={{ color: "var(--text-muted)" }}>Status: </span>
                      <span className="pill-badge active">{h.status ?? "—"}</span>
                    </div>
                  )}
                </div>
              ))}
              <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                <a className="link-arrow" href="#" onClick={(e) => e.preventDefault()}>View debt terms →</a>
              </div>
            </div>

            <div className="dashboard-card">
              <h3 className="section-title-blue">Share Class Details</h3>
              {data.shareClassDetails ? (
                <>
                  <span className="class-badge">{data.shareClassDetails.code}</span>
                  <span style={{ marginLeft: 10, fontWeight: 600 }}>{data.shareClassDetails.name}</span>
                  <div className="rights-grid">
                    {RIGHTS_ICONS.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="rights-item">
                        <div className="icon-badge purple"><Icon size={15} /></div>
                        <div>
                          <div className="rights-label">{label}</div>
                          <div className="rights-value">
                            {data.shareClassDetails![key] || "Not set yet"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 16, marginBottom: 12 }}>
                    Legal-rights terms will appear here once approved and entered in the ShareClasses sheet.
                  </p>
                  <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                    <a className="link-arrow" href="#" onClick={(e) => e.preventDefault()}>View full rights & terms →</a>
                  </div>
                </>
              ) : (
                <div className="empty-state">No share class on record.</div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h3 className="section-title-blue">Transactions History</h3>
            {data.transactions.length === 0 ? (
              <div className="empty-state">No transactions recorded yet.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Transaction</th><th>Description</th>
                    <th>Amount</th><th>Shares</th><th>Price / Share</th><th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t, i) => (
                    <tr key={i}>
                      <td>{formatDate(t.date)}</td>
                      <td>{t.type}</td>
                      <td>{t.description}</td>
                      <td>{t.amount ? formatMoney(t.amount) : "—"}</td>
                      <td>{t.shares ?? "—"}</td>
                      <td>{t.pricePerShare ? formatMoney(t.pricePerShare) : "—"}</td>
                      <td>
                        {t.documentAvailable ? (
                          <Download size={16} color="var(--accent)" style={{ cursor: "pointer" }} />
                        ) : (
                          <span style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}

function SummaryField({ label, value, green }: { label: string; value: string; green?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: green ? "#16a34a" : "inherit" }}>{value}</div>
    </div>
  );
}
